/**
 * Shared Cognito access-token refresh logic.
 *
 * Used by both api.ts (cloud-core client) and cloudIntersectionBrokerAPI.ts
 * (broker client). All instances share the same isRefreshing flag and
 * failedQueue, so a single in-flight refresh serves every client (Cognito
 * rotates refresh tokens; two parallel refreshes would invalidate each other).
 */
import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import { AUTH_STORAGE_KEYS, clearAuthTokens } from "./cognito";

const isDevelop =
  window._env_.REACT_APP_ENV === "development" ||
  window._env_.REACT_APP_ENV === "localhost";
const API_URL = !isDevelop
  ? `https://${window.location.host}/`
  : window._env_.REACT_APP_API_URL || "";
const API_VERSION = window._env_.REACT_APP_API_VERSION || "v1";
const REFRESH_ENDPOINT = `${API_URL}api/${API_VERSION}/login/cognito/refresh/`;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token !== null) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Refresh Cognito access token
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem(
    AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN
  );
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axios.post(REFRESH_ENDPOINT, {
    refresh_token: refreshToken,
  });

  // Note: Backend uses camelCase due to DRF CamelCase renderer
  const { accessToken, expiresIn } = response.data;
  const newToken = `Bearer ${accessToken}`;

  // Update stored tokens
  localStorage.setItem(AUTH_STORAGE_KEYS.JWT, newToken);
  const expiresAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(
    AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT,
    expiresAt.toString()
  );

  return newToken;
};

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Handle a 401 by refreshing the Cognito access token and retrying the
 * original request. Coalesces concurrent 401s across all axios instances:
 * only one refresh runs at a time; other failed requests queue and wait.
 *
 * On refresh failure, clears all auth tokens and redirects to /login.
 *
 * Callers should check the 401 + _retry + non-login-url conditions BEFORE
 * delegating to this helper (matches the original inline pattern in api.ts).
 */
export async function withCognitoRefresh(
  originalRequest: RetryableRequest,
  instance: AxiosInstance
): Promise<AxiosResponse> {
  if (isRefreshing) {
    // Queue this request until the in-flight refresh completes
    return new Promise<AxiosResponse>((resolve, reject) => {
      failedQueue.push({
        resolve: (token: string) => {
          originalRequest.headers.Authorization = token;
          resolve(instance(originalRequest));
        },
        reject: (err: Error) => {
          reject(err);
        },
      });
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const newToken = await refreshAccessToken();
    processQueue(null, newToken);
    originalRequest.headers.Authorization = newToken;
    return instance(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError as Error, null);
    clearAuthTokens();
    window.location.href = "/login";
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}
